import { useState, useEffect, useMemo } from 'react';
import { Inversion } from '../../../shared/types';
import { InversionRepository } from '../services/inversion.repository';
import { useAuth } from '../../auth/hooks/useAuth';
import { useInversionesMock } from './useInversionesMock';
import { LoggerService } from '../../../shared/services/logger.service';

export const useInversiones = () => {
  const { user } = useAuth();
  
  // Detectar si Firebase está configurado
  const isFirebaseConfigured = useMemo(() => {
    const hasApiKey = !!import.meta.env.VITE_FIREBASE_API_KEY;
    const hasProjectId = !!import.meta.env.VITE_FIREBASE_PROJECT_ID;
    return hasApiKey && hasProjectId;
  }, []);

  // Usar mock si Firebase no está configurado
  if (!isFirebaseConfigured) {
    LoggerService.info('Using mock inversiones (Firebase not configured)', 'useInversiones');
    return useInversionesMock();
  }

  const [inversiones, setInversiones] = useState<Inversion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const repository = useMemo(() => new InversionRepository(), []);

  useEffect(() => {
    if (!user) {
      setInversiones([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = repository.subscribeToInversiones(user.uid, (inversiones) => {
      setInversiones(inversiones);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const createInversion = async (inversion: Omit<Inversion, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      setError(null);
      await repository.create(user.uid, inversion);
    } catch (err: unknown) {
      const error = err as { message: string };
      setError(error.message);
      throw err;
    }
  };

  const updateInversion = async (id: string, inversion: Partial<Omit<Inversion, 'id' | 'createdAt' | 'updatedAt'>>) => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      setError(null);
      await repository.update(user.uid, id, inversion);
    } catch (err: unknown) {
      const error = err as { message: string };
      setError(error.message);
      throw err;
    }
  };

  const deleteInversion = async (id: string) => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      setError(null);
      await repository.delete(user.uid, id);
    } catch (err: unknown) {
      const error = err as { message: string };
      setError(error.message);
      throw err;
    }
  };

  const getInversionesByType = async (tipo: string): Promise<Inversion[]> => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      setError(null);
      return await repository.getByType(user.uid, tipo);
    } catch (err: unknown) {
      const error = err as { message: string };
      setError(error.message);
      throw err;
    }
  };

  const getInversionesByDateRange = async (startDate: Date, endDate: Date): Promise<Inversion[]> => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      setError(null);
      return await repository.getByDateRange(user.uid, startDate, endDate);
    } catch (err: unknown) {
      const error = err as { message: string };
      setError(error.message);
      throw err;
    }
  };

  const getTotalInversiones = (): number => {
    return inversiones.reduce((total, inversion) => total + inversion.monto, 0);
  };

  const getInversionesByMonth = (year: number, month: number): Inversion[] => {
    return inversiones.filter(inversion => {
      const fecha = inversion.fecha;
      return fecha.getFullYear() === year && fecha.getMonth() === month;
    });
  };

  const getInversionesByYear = (year: number): Inversion[] => {
    return inversiones.filter(inversion => inversion.fecha.getFullYear() === year);
  };

  const getTypes = (): string[] => {
    const types = new Set(inversiones.map(inversion => inversion.tipo));
    return Array.from(types).sort();
  };

  const getTypeTotals = (): { [tipo: string]: number } => {
    const totals: { [tipo: string]: number } = {};
    inversiones.forEach(inversion => {
      totals[inversion.tipo] = (totals[inversion.tipo] || 0) + inversion.monto;
    });
    return totals;
  };

  return {
    inversiones,
    loading,
    error,
    createInversion,
    updateInversion,
    deleteInversion,
    getInversionesByType,
    getInversionesByDateRange,
    getTotalInversiones,
    getInversionesByMonth,
    getInversionesByYear,
    getTypes,
    getTypeTotals,
  };
};
